import {useEffect, useState} from 'react';
import {useMutation, useQuery} from '@apollo/client';
import {Switch, Tooltip, message} from 'antd';
import {BellOutlined, QuestionCircleOutlined} from '@ant-design/icons';
import {AnimatePresence, motion} from 'framer-motion';
import styles from './AdminPushToggle.module.scss';
import {GET_ADMIN_PUSH_CONFIG} from 'graphql/queries/aiHelpdesk';
import {
  REGISTER_ADMIN_PUSH_SUBSCRIPTION,
  UNREGISTER_ADMIN_PUSH_SUBSCRIPTION,
} from 'graphql/mutations/aiHelpdesk';
import {
  ADMIN_PUSH_SW_URL,
  getPushSubscription,
  isPushSupported,
  pushSubscribeErrorMessage,
  urlBase64ToUint8Array,
} from 'utils/adminPush';
import {useTicketAlertsStore} from '../../zustand/ticketAlertsStore';

const HELP = (
  <div>
    <p style={{margin: '0 0 6px'}}>
      When this is on, the dashboard beeps whenever a customer replies on one of
      your tickets or a ticket is escalated, and your browser shows a
      notification for replies even with this tab closed. Turn it on once in
      each browser you use, then click Allow when asked. Turn it off and all of
      it stops.
    </p>
    <p style={{margin: '0 0 4px', fontWeight: 600}}>Not working?</p>
    <ul className={styles.helpList}>
      <li>
        Allow notifications for this site in the browser&apos;s site settings.
      </li>
      <li>
        Brave: turn on &ldquo;Use Google services for push messaging&rdquo; in
        brave://settings/privacy.
      </li>
      <li>Use Chrome, Edge or Firefox, not an in-app browser.</li>
      <li>Check macOS Focus / Do Not Disturb or Windows Focus Assist.</li>
    </ul>
  </div>
);

const LABEL = 'Customer reply alerts';

const configInput = endpoint => ({input: endpoint ? {endpoint} : {}});

// The whole row slides open/closed rather than popping in once the config loads.
const ROW_MOTION = {
  initial: {opacity: 0, height: 0, marginTop: 0, paddingTop: 0},
  animate: {opacity: 1, height: 'auto', marginTop: 12, paddingTop: 12},
  exit: {opacity: 0, height: 0, marginTop: 0, paddingTop: 0},
  transition: {duration: 0.25, ease: 'easeOut'},
};

/**
 * "Customer reply alerts" for this browser, on the bottom row of the dashboard
 * top bar for anyone with Customer Support access. On = in-dashboard beep +
 * browser push for replies waiting on you; off = neither. Hidden when the
 * browser can't do push, or when the server has push switched off (`enabled`
 * false, or an Error such as 503).
 */
export const AdminPushToggle = ({className}) => {
  // undefined = still reading this browser's subscription.
  const [endpoint, setEndpoint] = useState(undefined);
  // What's in progress, shown in place of the label: {text, target} or null.
  const [pending, setPending] = useState(null);
  const setReplyAlertsOn = useTicketAlertsStore(
    state => state.setReplyAlertsOn,
  );

  useEffect(() => {
    if (!isPushSupported()) return;
    getPushSubscription().then(sub => setEndpoint(sub?.endpoint || null));
  }, []);

  const {data, previousData, refetch} = useQuery(GET_ADMIN_PUSH_CONFIG, {
    variables: configInput(endpoint),
    skip: endpoint === undefined,
    fetchPolicy: 'no-cache',
    errorPolicy: 'all',
  });
  const [registerMut] = useMutation(REGISTER_ADMIN_PUSH_SUBSCRIPTION);
  const [unregisterMut] = useMutation(UNREGISTER_ADMIN_PUSH_SUBSCRIPTION);

  // Keep showing the last answer while a refetch is in flight, so the row
  // doesn't vanish and reappear after every flip.
  const node = (data || previousData)?.getAdminPushConfig;
  const status =
    node?.__typename === 'ResponseWithAdminPushStatus' ? node.data : null;
  const on = !!status?.enabled && !!status?.subscribed;

  useEffect(() => {
    if (status) setReplyAlertsOn(on);
  }, [status, on, setReplyAlertsOn]);

  const resync = next => {
    setEndpoint(next);
    refetch(configInput(next));
  };

  // Must run straight from the click: browsers only show the permission
  // prompt on a user gesture.
  const turnOn = async () => {
    setPending({text: 'Waiting for permission…', target: true});
    try {
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') {
        message.warning(
          "Notifications are blocked — allow them in the browser's site settings",
        );
        return;
      }
      setPending({text: 'Turning on…', target: true});
      const reg = await navigator.serviceWorker.register(ADMIN_PUSH_SW_URL, {
        scope: '/',
      });
      await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        try {
          sub = await reg.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(status.vapidPublicKey),
          });
        } catch (err) {
          message.error(pushSubscribeErrorMessage(err), 8);
          return;
        }
      }
      const {endpoint: next, keys} = sub.toJSON();
      const res = await registerMut({
        variables: {
          input: {
            endpoint: next,
            keys: {p256dh: keys.p256dh, auth: keys.auth},
            userAgent: navigator.userAgent,
          },
        },
      });
      const result = res.data?.registerAdminPushSubscription;
      if (result?.__typename === 'Error') {
        message.error(result.message);
        return;
      }
      setReplyAlertsOn(true);
      message.success('Customer reply alerts are on for this browser');
      resync(next);
    } catch (err) {
      message.error(err.message);
    } finally {
      setPending(null);
    }
  };

  const turnOff = async () => {
    setPending({text: 'Turning off…', target: false});
    try {
      const sub = await getPushSubscription();
      if (sub) {
        const res = await unregisterMut({
          variables: {input: {endpoint: sub.endpoint}},
        });
        const result = res.data?.unregisterAdminPushSubscription;
        if (result?.__typename === 'Error') {
          message.error(result.message);
          return;
        }
        await sub.unsubscribe();
      }
      setReplyAlertsOn(false);
      message.success('Customer reply alerts are off for this browser');
      resync(null);
    } catch (err) {
      message.error(err.message);
    } finally {
      setPending(null);
    }
  };

  const label = pending?.text || LABEL;

  return (
    <AnimatePresence>
      {status?.enabled ? (
        <motion.div
          key="reply-alerts"
          className={className}
          style={{overflow: 'hidden'}}
          {...ROW_MOTION}>
          <div className={styles.pushToggle} aria-busy={!!pending}>
            <BellOutlined className={styles.pushIcon} />
            <label htmlFor="admin-push-switch" className={styles.label}>
              <motion.span
                key={label}
                initial={{opacity: 0, y: 4}}
                animate={{opacity: 1, y: 0}}
                transition={{duration: 0.18}}
                className={pending ? styles.pendingText : undefined}>
                {label}
              </motion.span>
            </label>
            <Tooltip
              title={HELP}
              color="#1e293b"
              placement="bottomRight"
              overlayStyle={{maxWidth: 340}}>
              <QuestionCircleOutlined
                className={styles.pushHelp}
                aria-label="How customer reply alerts work"
                tabIndex={0}
              />
            </Tooltip>
            <Switch
              id="admin-push-switch"
              aria-label={LABEL}
              size="small"
              checked={pending ? pending.target : on}
              loading={!!pending}
              disabled={!!pending}
              onChange={checked => (checked ? turnOn() : turnOff())}
            />
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};
